import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate, downloadBlob } from "../lib/api";
import Topbar from "../components/Topbar";
import EmptyState from "../components/EmptyState";
import PizzaSpinner from "../components/PizzaSpinner";
import { Download, FileCheck, Receipt, Landmark } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trans, useTranslation } from "react-i18next";

export default function SupplierReceipts() {
  const { t } = useTranslation();
  const [payments, setPayments] = useState(null);

  useEffect(() => {
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => {});
  }, []);

  const downloadReceipt = async (p) => {
    try {
      const res = await api.get(`/payments/${p.id}/receipt`, { responseType: "blob" });
      downloadBlob(res.data, p.receipt_filename || `comprobante-${p.id.slice(0, 8)}.pdf`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  if (!payments) return <><Topbar title={t("supplierReceipts.title")} /><PizzaSpinner /></>;

  const total = payments.reduce((a, b) => a + b.amount, 0);

  return (
    <>
      <Topbar title={t("supplierReceipts.title")} subtitle={t("supplierReceipts.subtitle")} />
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-[#FFDF00] to-[#FFC700] rounded-2xl p-6 text-[#1A1A1A]">
            <div className="text-xs uppercase tracking-wider font-bold">{t("supplierReceipts.totalCollected")}</div>
            <div className="text-3xl font-display font-extrabold mt-2">{fmtMXN(total)}</div>
            <div className="text-xs font-semibold mt-1">{t("supplierReceipts.paymentsReceived", { count: payments.length })}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{t("supplierReceipts.lastPayment")}</div>
            <div className="text-3xl font-display font-extrabold mt-2 text-[#1A1A1A]">
              {payments.length > 0 ? fmtMXN(payments[0].amount) : "—"}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {payments.length > 0 ? fmtDate(payments[0].payment_date) : t("supplierReceipts.noPaymentsYet")}
            </div>
          </div>
        </div>

        {payments.length === 0 ? (
          <EmptyState title={t("supplierReceipts.emptyTitle")} message={t("supplierReceipts.emptyMsg")} icon={Receipt} />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.tableInvoice")}</th>
                    <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.amount")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierReceipts.tableMethod")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("supplierDetail.tableReference")}</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, idx) => (
                    <motion.tr key={p.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-6 py-3 text-sm">{fmtDate(p.payment_date)}</td>
                      <td className="px-6 py-3 text-sm font-mono">{p.invoice_folio}</td>
                      <td className="px-6 py-3 text-base font-bold text-right text-emerald-700">{fmtMXN(p.amount)}</td>
                      <td className="px-6 py-3 text-xs">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded-full font-semibold capitalize">
                          {p.payment_method === "transfer" ? t("supplierReceipts.transfer") : p.payment_method}
                        </span>
                        {/* Fix WH77 (2026-10-14): el pago ya aparecio en el archivo del banco */}
                        {p.bank_confirmed_at && (
                          <span data-testid={`receipt-bank-confirmed-${p.id}`} title={t("payments.bankConfirmedTitle", { date: fmtDate(p.bank_confirmed_at) })}
                            className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                            <Landmark className="w-3 h-3" /> {t("payments.bankConfirmed")}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-xs font-mono text-slate-500">{p.bank_reference || "—"}</td>
                      <td className="px-6 py-3 text-right">
                        {p.has_receipt ? (
                          <button onClick={() => downloadReceipt(p)}
                            data-testid={`download-receipt-${p.id}`}
                            className="btn-primary-chizy px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ml-auto">
                            <Download className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("supplierReceipts.receiptBtn")}
                          </button>
                        ) : p.source === "archivo_banco" ? (
                          // Fix WH77 (2026-10-14): su respaldo es el movimiento del banco
                          <span data-testid={`receipt-bank-${p.id}`}
                            className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ml-auto w-fit bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                            <Landmark className="w-3.5 h-3.5" /> {t("payments.fromBankFile")}
                          </span>
                        ) : (
                          <button disabled title={t("payments.noReceiptAttached")}
                            data-testid={`download-receipt-${p.id}`}
                            className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 ml-auto bg-slate-100 text-slate-400 cursor-not-allowed">
                            <Download className="w-3.5 h-3.5" /> {t("payments.noReceiptAttached")}
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="bg-[#FFFBE6] border border-[#FFDF00] rounded-xl p-4 flex items-start gap-3">
          <FileCheck className="w-5 h-5 text-[#1A1A1A] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-700">
            <strong className="block text-[#1A1A1A] mb-0.5">{t("supplierReceipts.tipTitle")}</strong>
            <Trans i18nKey="supplierReceipts.tipBody" components={{ strong: <strong /> }} />
          </div>
        </div>
      </div>
    </>
  );
}
